import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

export default function AvailabilityPage() {
  const { member } = useAuth();
  const [match, setMatch] = useState(null);
  const [availWindow, setAvailWindow] = useState(null);
  // Every upcoming fixture, not just the nearest. A series runs several
  // matches, each with its own date, venue and availability window, and a
  // member answers each one separately.
  const [fixtures, setFixtures]       = useState([]);
  const [selectedId, setSelectedId]   = useState(null);
  const [nextOpening, setNextOpening] = useState(null);
  const [myResponse, setMyResponse] = useState(null);
  const [gameResponse, setGameResponse] = useState(null);
  const [lunchResponse, setLunchResponse] = useState(null);
  // Special requests (dynamic, fetched from /api/squad-config/special-conditions)
  const [specialConditions, setSpecialConditions] = useState([]); // [{ id, code, label, is_standby, input_type, input_label }]
  const [hasSpecial, setHasSpecial] = useState(false);
  // Map of condition_id -> { checked, member_input }
  const [requestState, setRequestState] = useState({});
  // Opt-out reason modal
  const [showOptOutModal, setShowOptOutModal] = useState(false);
  const [optOutReason, setOptOutReason] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [optedOut, setOptedOut] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [optOutStatus, setOptOutStatus] = useState(null); // { window_open, yes_count, over_limit, needed }
  const [lunchMenu, setLunchMenu] = useState(null); // { menu_text, menu_image_url }

  useEffect(() => { loadAvailability(); }, []);

  // Re-check opt-out status every 60s — window opens at 6PM Friday
  useEffect(() => {
    const interval = setInterval(async () => {
      if (!match) return;
      try {
        const r = await api.get(`/availability/opt-out-status/${match.id}`);
        setOptOutStatus(r.data);
      } catch {}
    }, 60000);
    return () => clearInterval(interval);
  }, [match?.id]);

  async function loadAvailability(matchIdOverride) {
    try {
      // One call gives every upcoming fixture with its own window, venue and
      // the member's answer so far. Replaces /matches/upcoming-sunday, which
      // returned a single match and so hid the rest of a series entirely.
      let all = [];
      try {
        const lr = await api.get('/availability/upcoming/list');
        all = lr.data.matches || [];
      } catch { all = []; }

      // Only matches you can actually answer for right now (SB, 3 Sep 2026).
      // Listing a fixture whose window is shut — or has not opened — invites a
      // member to tap it and find they cannot reply, and after a series window
      // closes the page should be empty rather than showing five settled
      // fixtures as though something is owed.
      // Open AND not already locked. An admin can lock responses before the
      // window's own deadline — after that the member cannot change anything,
      // so listing the fixture under "to answer for" is simply untrue. This is
      // why the page still offered five settled UPL fixtures: the series
      // deadline had not passed, but the answers were locked.
      const list = all.filter(f => f.window?.is_open && !f.is_locked);
      setFixtures(list);

      // The soonest window still to open, so the empty state can say when
      // rather than just "nothing here".
      const upcomingOpen = all
        .filter(f => f.window?.is_before && f.window?.open_at)
        .sort((a, b) => new Date(a.window.open_at) - new Date(b.window.open_at))[0] || null;
      setNextOpening(upcomingOpen);

      const chosen =
        (matchIdOverride && list.find(f => f.id === matchIdOverride)) ||
        list[0] || null;

      let upcomingMatch = chosen;
      if (chosen) {
        setSelectedId(chosen.id);
        setAvailWindow(chosen.window || null);
        // The card needs fields the list doesn't carry (cancel reason, series
        // format and so on), so fetch the full row for the chosen fixture.
        try {
          const full = await api.get('/matches/' + chosen.id);
          if (full.data?.match) upcomingMatch = { ...chosen, ...full.data.match };
        } catch { /* the list row is enough to render */ }
      } else {
        setAvailWindow(null);
      }
      setMatch(upcomingMatch);

      if (upcomingMatch) {
        const [avRes, optOutRes, lunchRes, condRes] = await Promise.allSettled([
          api.get(`/availability/${upcomingMatch.id}`),
          api.get(`/availability/opt-out-status/${upcomingMatch.id}`),
          api.get(`/lunch/${upcomingMatch.id}`),
          api.get('/squad-config/special-conditions'),
        ]);

        // Special conditions first (needed to interpret response)
        const conds = (condRes.status === 'fulfilled' && condRes.value.data.conditions) || [];
        setSpecialConditions(conds);

        if (avRes.status === 'fulfilled') {
          const resp = avRes.value.data.myResponse;
          setMyResponse(resp);
          if (resp) {
            setGameResponse(resp.game_response);
            setLunchResponse(resp.lunch_response);
            // Hydrate request state from special_requests array (Phase 2b format)
            // Fall back to legacy booleans if special_requests is empty (from a pre-Phase-2b submission)
            const newState = {};
            let anyTicked = false;
            if (Array.isArray(resp.special_requests) && resp.special_requests.length > 0) {
              for (const sr of resp.special_requests) {
                newState[sr.condition_id] = {
                  checked: true,
                  member_input: sr.member_input || '',
                };
                anyTicked = true;
              }
            } else {
              // Legacy fallback — match Phase 1 columns to conditions by code
              const codeToCond = new Map(conds.map(c => [c.code, c]));
              const legacy = [
                { code: 'no_bowling',    flag: !!resp.no_bowling },
                { code: 'no_keeping',    flag: !!resp.no_keeping },
                { code: 'no_captaincy',  flag: !!resp.no_captaincy },
                { code: 'leaving_early', flag: !!resp.leaving_early, input: resp.leave_time || '' },
              ];
              for (const l of legacy) {
                if (!l.flag) continue;
                const c = codeToCond.get(l.code);
                if (!c) continue;
                newState[c.id] = { checked: true, member_input: l.input || '' };
                anyTicked = true;
              }
            }
            setRequestState(newState);
            setHasSpecial(anyTicked);
          }
        }
        if (optOutRes.status === 'fulfilled') {
          setOptOutStatus(optOutRes.value.data);
        }
        if (lunchRes.status === 'fulfilled' && lunchRes.value.data.menu) {
          setLunchMenu(lunchRes.value.data.menu);
        }
        try {
          const optRes = await api.get(`/availability/opt-outs/${upcomingMatch.id}`);
          setOptedOut(optRes.data.hasOptedOut);
        } catch {}
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit() {
    if (!gameResponse) return toast.error('Please select Yes or No for the match');
    if (!lunchResponse) return toast.error('Please select Yes or No for lunch');
    if (!match) return;

    // Build the dynamic special_requests array — only if YES + has-special is ticked
    let special_requests = [];
    if (gameResponse === 'yes' && hasSpecial) {
      for (const c of specialConditions) {
        const s = requestState[c.id];
        if (!s || !s.checked) continue;
        if (c.input_type !== 'none' && (!s.member_input || String(s.member_input).trim() === '')) {
          const fieldName = c.input_label || (c.input_type === 'time' ? 'time' : 'details');
          return toast.error(`Please fill in the ${fieldName} for "${c.label}"`);
        }
        special_requests.push({
          condition_id: c.id,
          member_input: c.input_type !== 'none' ? String(s.member_input).trim() : null,
        });
      }
    }

    setSubmitting(true);
    try {
      const res = await api.post('/availability', {
        match_id: match.id,
        game_response: gameResponse,
        lunch_response: lunchResponse,
        special_requests,
      });
      setSubmitted(true);
      toast.success(res.data?.message || 'Availability saved ✅', { duration: 5000 });
      loadAvailability();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleOptOut() {
    if (!match) return;
    // If already opted out, cancelling doesn't need a reason
    if (optedOut) {
      setSubmitting(true);
      try {
        await api.post('/availability/opt-out/cancel', { match_id: match.id });
        setOptedOut(false);
        toast.success('Opt Out cancelled');
      } catch (err) {
        toast.error(err.response?.data?.error || 'Failed');
      } finally {
        setSubmitting(false);
      }
      return;
    }
    // Not yet opted out — open reason modal
    setOptOutReason('');
    setShowOptOutModal(true);
  }

  async function submitOptOut() {
    if (!optOutReason.trim() || optOutReason.trim().length < 3) {
      return toast.error('Please provide a reason for opting out');
    }
    setSubmitting(true);
    try {
      const res = await api.post('/availability/opt-out', {
        match_id: match.id,
        reason: optOutReason.trim(),
      });
      setOptedOut(true);
      setShowOptOutModal(false);
      setOptOutReason('');
      toast.success(res.data?.message || 'Opt-Out request recorded.', { duration: 6000 });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to opt out');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'60vh' }}>
      <div style={{ textAlign:'center' }}>
        <div style={{ width:40, height:40, border:'3px solid #F5C518', borderTopColor:'transparent', borderRadius:'50%', animation:'spin 0.8s linear infinite', margin:'0 auto 12px' }} />
        <div style={{ color:'#6b7280', fontSize:'0.85rem' }}>Loading...</div>
      </div>
    </div>
  );

  const matchDate = match ? new Date(match.match_date) : null;
  const isLocked = myResponse?.is_locked;
  const isSeriesMatch = match?.series_id;
  // A series fixture carries its own explicit window now, so it is no longer a
  // special case that bypasses the check. If no window has been set for it the
  // backend reports the window closed with a reason, which is what shows here.
  const windowOpen   = !availWindow ? true : !!availWindow.is_open;
  const windowBefore = !!availWindow?.is_before;
  const windowClosed = !!availWindow?.is_closed;
  const canSubmit    = windowOpen && !isLocked && !windowClosed;
  // Derive opt-out window from timestamps directly so stale state doesn't block it
  const isOptOutWindowOpen = (() => {
    if (!optOutStatus) return false;
    if (optOutStatus.opt_out_open_at && optOutStatus.opt_out_close_at) {
      const now = new Date();
      return now >= new Date(optOutStatus.opt_out_open_at) && now <= new Date(optOutStatus.opt_out_close_at);
    }
    return optOutStatus.window_open || false;
  })();
  // Opt-out: member said YES + opt-out window open + yes count > 26
  const canOptOut    = gameResponse === 'yes' && myResponse && isOptOutWindowOpen && optOutStatus?.over_limit;

  const opensAt  = availWindow?.open_at  ? new Date(availWindow.open_at)  : null;
  const closesAt = availWindow?.close_at ? new Date(availWindow.close_at) : null;
  const now = new Date();
  const minsToOpen  = opensAt  ? Math.max(0, Math.ceil((opensAt - now) / 60000)) : 0;
  const hoursToOpen = Math.floor(minsToOpen / 60);
  const minsRem     = minsToOpen % 60;
  const opensTimeStr  = opensAt  ? opensAt.toLocaleTimeString('en-IN',  { hour:'2-digit', minute:'2-digit' }) : '10:30 AM';
  const closesTimeStr = closesAt ? closesAt.toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit' }) : '6:00 PM';
  const opensDayStr   = opensAt  ? opensAt.toLocaleDateString('en-IN',  { weekday:'long', day:'numeric', month:'short' }) : 'Friday';

  const matchDayStr = matchDate ? matchDate.toLocaleDateString('en-IN', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
  }) : '';

  // ── Styles ───────────────────────────────────────────────
  const NAVY   = '#1E3A6E';
  const GOLD   = '#F5C518';
  const GREEN  = '#16a34a';
  const RED    = '#dc2626';
  const LIGHT  = '#f8fafc';

  const sectionStyle = {
    background: 'white',
    borderRadius: 16,
    marginBottom: 16,
    overflow: 'hidden',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.05)',
  };

  const sectionHeaderStyle = (color = NAVY) => ({
    padding: '14px 18px 12px',
    borderBottom: '1px solid #f1f5f9',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  });

  const labelStyle = {
    fontFamily: 'Rajdhani, sans-serif',
    fontWeight: 700,
    fontSize: '0.8rem',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: '#94a3b8',
  };

  function ResponseBtn({ value, selected, onClick, disabled, yesNo }) {
    const isYes = yesNo === 'yes';
    const activeColor = isYes ? GREEN : RED;
    const activeBg    = isYes ? '#f0fdf4' : '#fef2f2';
    const activeBorder= isYes ? '#86efac' : '#fca5a5';
    const isActive = selected === value;

    return (
      <button
        onClick={() => !disabled && onClick(value)}
        disabled={disabled}
        style={{
          flex: 1,
          padding: '14px 0',
          borderRadius: 12,
          border: isActive ? `2px solid ${activeBorder}` : '2px solid #e2e8f0',
          background: isActive ? activeBg : '#f8fafc',
          color: isActive ? activeColor : '#94a3b8',
          fontFamily: 'Rajdhani, sans-serif',
          fontWeight: 800,
          fontSize: '1rem',
          letterSpacing: '0.05em',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled && !isActive ? 0.4 : 1,
          transition: 'all 0.15s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
        }}
      >
        <span style={{ fontSize: '1.1rem' }}>{isYes ? '✅' : '❌'}</span>
        {isYes ? 'YES' : 'NO'}
      </button>
    );
  }

  return (
    <div style={{ background: LIGHT, minHeight: '100vh' }}>

      {/* ── Opt-Out Reason Modal ── */}
      {showOptOutModal && (
        <div
          onClick={() => !submitting && setShowOptOutModal(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
            display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
            // B12 v2 — the AppShell bottom-nav is position:fixed at the bottom
            // and overlaps anything aligned with flex-end. Push the backdrop's
            // usable area up by the nav height + safe-area + keyboard inset
            // so the modal (and its submit button) sit above the nav.
            paddingBottom: 'calc(var(--nav-height, 64px) + env(safe-area-inset-bottom, 0px) + env(keyboard-inset-height, 0px))',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'white', width: '100%', maxWidth: 500,
              borderTopLeftRadius: 20, borderTopRightRadius: 20,
              // B12 v2 — clamp to visible viewport (dvh on mobile).
              maxHeight: '80dvh',
              boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
              // Flex column so textarea scrolls inside and buttons stick to bottom.
              display: 'flex', flexDirection: 'column',
            }}
          >
            {/* Scrollable content area */}
            <div style={{
              flex: '1 1 auto', minHeight: 0,
              overflowY: 'auto', WebkitOverflowScrolling: 'touch',
              padding: '22px 20px 12px',
            }}>
              <div style={{ textAlign: 'center', marginBottom: 16 }}>
                <div style={{ fontSize: '2rem', marginBottom: 6 }}>🙋</div>
                <div style={{ fontFamily: 'Rajdhani, sans-serif', fontWeight: 700, fontSize: '1.15rem', color: NAVY }}>
                  Volunteer to Opt Out
                </div>
                <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: 4 }}>
                  Please share why you'd like to sit out
                </div>
              </div>
              <textarea
                rows={3}
                placeholder="Reason (required) — e.g. Family function, injury, work commitment..."
                value={optOutReason}
                onChange={e => setOptOutReason(e.target.value)}
                maxLength={300}
                autoFocus
                style={{
                  width: '100%', padding: '11px 13px',
                  border: '1.5px solid #e2e8f0', borderRadius: 10, fontSize: '0.9rem',
                  fontFamily: 'DM Sans, sans-serif', color: NAVY,
                  resize: 'none', outline: 'none', boxSizing: 'border-box',
                  background: '#f8fafc', lineHeight: 1.5,
                }}
              />
              <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: 4, marginBottom: 4 }}>
                Your reason is visible only to admins.
              </div>
            </div>
            {/* Sticky action row */}
            <div style={{
              flex: '0 0 auto',
              display: 'flex', gap: 10,
              padding: '14px 20px 16px',
              borderTop: '1px solid #f1f5f9',
              background: 'white',
            }}>
              <button
                onClick={() => !submitting && setShowOptOutModal(false)}
                disabled={submitting}
                style={{
                  flex: 1, padding: '12px', borderRadius: 10,
                  border: '1.5px solid #e2e8f0', background: 'white', color: '#64748b',
                  fontFamily: 'Rajdhani, sans-serif', fontWeight: 700, fontSize: '0.88rem',
                  letterSpacing: '0.05em', textTransform: 'uppercase', cursor: 'pointer',
                }}
              >Cancel</button>
              <button
                onClick={submitOptOut}
                disabled={submitting || optOutReason.trim().length < 3}
                style={{
                  flex: 1, padding: '12px', borderRadius: 10, border: 'none',
                  background: (submitting || optOutReason.trim().length < 3) ? '#e2e8f0' : GOLD,
                  color: (submitting || optOutReason.trim().length < 3) ? '#94a3b8' : NAVY,
                  fontFamily: 'Rajdhani, sans-serif', fontWeight: 800, fontSize: '0.88rem',
                  letterSpacing: '0.05em', textTransform: 'uppercase',
                  cursor: (submitting || optOutReason.trim().length < 3) ? 'not-allowed' : 'pointer',
                }}
              >{submitting ? 'Submitting…' : 'Submit Request'}</button>
            </div>
          </div>
        </div>
      )}
      {/* ── Unified Hero Header ── */}
      <div style={{
        background: `linear-gradient(135deg, ${NAVY} 0%, #0f2347 100%)`,
        padding: '22px 20px 24px',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', right: -20, top: -20, width: 120, height: 120, borderRadius: '50%', background: 'rgba(245,197,24,0.06)', border: '1px solid rgba(245,197,24,0.1)' }} />
        <div style={{ position: 'absolute', right: 20, top: 20, width: 60, height: 60, borderRadius: '50%', background: 'rgba(245,197,24,0.08)', border: '1px solid rgba(245,197,24,0.12)' }} />

        <div style={{ position: 'relative' }}>
          <div style={{ ...labelStyle, color: 'rgba(245,197,24,0.7)', marginBottom: 4 }}>Season 2026</div>
          <h1 style={{ fontFamily: 'Rajdhani, sans-serif', fontWeight: 800, fontSize: '1.8rem', color: GOLD, margin: '0 0 6px', letterSpacing: '0.02em' }}>
            Availability
          </h1>

          {matchDate && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
              <span style={{ color: 'white', fontSize: '0.95rem', fontWeight: 600 }}>
                📅 {matchDayStr}
              </span>
              {match.series_name && (
                <span style={{ background: 'rgba(245,197,24,0.2)', color: GOLD, padding: '2px 10px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.05em' }}>
                  {match.series_name}
                </span>
              )}
            </div>
          )}

          {/* Window status inline in header */}
          {matchDate && !match.is_cancelled && !isSeriesMatch && (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: windowOpen ? 'rgba(34,197,94,0.15)' : windowClosed ? 'rgba(239,68,68,0.15)' : 'rgba(245,197,24,0.12)',
              border: `1px solid ${windowOpen ? 'rgba(34,197,94,0.3)' : windowClosed ? 'rgba(239,68,68,0.3)' : 'rgba(245,197,24,0.25)'}`,
              borderRadius: 20, padding: '5px 12px',
            }}>
              <div style={{
                width: 7, height: 7, borderRadius: '50%',
                background: windowOpen ? '#22c55e' : windowClosed ? '#ef4444' : GOLD,
                boxShadow: windowOpen ? '0 0 6px #22c55e' : 'none',
              }} />
              <span style={{ fontSize: '0.78rem', fontWeight: 600, color: windowOpen ? '#86efac' : windowClosed ? '#fca5a5' : GOLD }}>
                {windowOpen && !isLocked && `Open · Closes ${closesTimeStr} Friday`}
                {(windowClosed || isLocked) && `Closed · ${closesTimeStr} Friday`}
                {windowBefore && (opensAt
                  ? `Opens ${opensAt.toLocaleDateString('en-IN', { weekday:'short', day:'numeric', month:'short' })} at ${opensTimeStr}`
                  : `Opens Friday at ${opensTimeStr}`
                )}
              </span>
              {windowBefore && minsToOpen > 0 && (
                <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)', marginLeft: 2 }}>
                  ({hoursToOpen > 0 ? `${hoursToOpen}h ` : ''}{minsRem}m)
                </span>
              )}
            </div>
          )}

          {!matchDate && (
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.88rem', margin: 0 }}>No match scheduled</p>
          )}
        </div>
      </div>

      <div style={{ padding: '16px 16px 100px' }}>

        {/* ── Fixture picker ──
             Only appears when there is more than one match to answer for —
             i.e. a series. Each row carries the date, reporting time and
             venue, because a series fixture is not always at the club ground. */}
        {fixtures.length > 1 && (
          <div style={{ ...sectionStyle, marginBottom: 14, overflow: 'hidden' }}>
            <div style={{ ...sectionHeaderStyle(), paddingBottom: 10 }}>
              <span style={{ fontSize: '1rem' }}>🗓️</span>
              <div style={{ fontFamily: 'Rajdhani, sans-serif', fontWeight: 700, fontSize: '0.9rem', color: NAVY }}>
                {fixtures.length} matches to answer for
              </div>
            </div>
            <div style={{ padding: '0 12px 12px' }}>
              {fixtures.map(f => {
                const active = f.id === selectedId;
                const d = new Date(f.match_date);
                const answered = !!f.my_response;
                const open = f.window?.is_open;
                return (
                  <button key={f.id} onClick={() => { if (!active) loadAvailability(f.id); }}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left', cursor: active ? 'default' : 'pointer',
                      background: active ? 'rgba(30,58,110,0.04)' : 'white',
                      border: `1.5px solid ${active ? NAVY : '#e5e7eb'}`,
                      borderRadius: 10, padding: '10px 12px', marginTop: 8, fontFamily: 'inherit',
                    }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontFamily: 'Rajdhani, sans-serif', fontWeight: 800, fontSize: '0.95rem', color: NAVY }}>
                          {d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}
                          <span style={{ color: '#94a3b8', fontWeight: 600, fontSize: '0.8rem' }}>
                            {' · '}{(f.reporting_time || '07:30').slice(0, 5)}
                          </span>
                        </div>
                        <div style={{ fontSize: '0.76rem', color: '#64748b', marginTop: 2 }}>
                          📍 {f.venue || 'UCC Ground'}
                        </div>
                        {f.series_name && (
                          <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: 2 }}>{f.series_name}</div>
                        )}
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        {answered ? (
                          <span style={{
                            fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.05em',
                            padding: '3px 8px', borderRadius: 10,
                            background: f.my_response === 'yes' ? '#dcfce7' : '#fee2e2',
                            color:      f.my_response === 'yes' ? '#166534' : '#991b1b',
                          }}>{f.my_response.toUpperCase()}</span>
                        ) : (
                          <span style={{
                            fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.05em',
                            padding: '3px 8px', borderRadius: 10,
                            background: open ? '#fef3c7' : '#f1f5f9',
                            color:      open ? '#92400e' : '#94a3b8',
                          }}>{open ? 'TO ANSWER' : '—'}</span>
                        )}
                        {!open && f.window?.open_at && (
                          <div style={{ fontSize: '0.64rem', color: '#94a3b8', marginTop: 3 }}>
                            opens {new Date(f.window.open_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                          </div>
                        )}
                        {!open && !f.window?.open_at && (
                          <div style={{ fontSize: '0.64rem', color: '#b45309', marginTop: 3 }}>no window set</div>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ── No Match ── */}
        {!match && (
          <div style={{ ...sectionStyle, padding: 32, textAlign: 'center' }}>
            <div style={{ fontSize: '3rem', marginBottom: 12 }}>✅</div>
            <div style={{ fontFamily: 'Rajdhani, sans-serif', fontWeight: 700, fontSize: '1.1rem', color: NAVY, marginBottom: 4 }}>
              Nothing to answer right now
            </div>
            <div style={{ color: '#94a3b8', fontSize: '0.85rem', lineHeight: 1.6 }}>
              {nextOpening ? (
                <>
                  The next availability window opens{' '}
                  <strong style={{ color: NAVY }}>
                    {new Date(nextOpening.window.open_at).toLocaleString('en-IN', {
                      weekday: 'long', day: 'numeric', month: 'short',
                      hour: 'numeric', minute: '2-digit', hour12: true,
                    })}
                  </strong>
                  {' '}for {new Date(nextOpening.match_date).toLocaleDateString('en-IN',
                    { weekday: 'long', day: 'numeric', month: 'short' })}.
                  <br />You'll get a notification when it does.
                </>
              ) : (
                <>No availability request is open. You'll be notified when the next one goes out.</>
              )}
            </div>
          </div>
        )}

        {/* ── Cancelled ── */}
        {match?.is_cancelled && (
          <div style={{
            ...sectionStyle,
            border: `1px solid #fca5a5`,
            background: '#fef2f2',
            padding: '16px 18px',
            display: 'flex', gap: 12, alignItems: 'flex-start',
          }}>
            <span style={{ fontSize: '1.3rem' }}>🚫</span>
            <div>
              <div style={{ fontFamily: 'Rajdhani, sans-serif', fontWeight: 700, color: RED }}>Match Cancelled</div>
              <div style={{ fontSize: '0.83rem', color: '#9f1239', marginTop: 2 }}>{match.cancel_reason || 'No reason provided'}</div>
            </div>
          </div>
        )}

        {match && !match.is_cancelled && (
          <>
            {/* Response status — compact, below header */}
            {(windowClosed || isLocked) && myResponse && (
              <div style={{ ...sectionStyle, border: '1px solid #fde68a', background: '#fffbeb', padding: '12px 16px', display: 'flex', gap: 10, alignItems: 'center' }}>
                <span>🔒</span>
                <span style={{ fontSize: '0.82rem', color: '#92400e' }}>
                  Window closed. Your response: <strong>{myResponse.game_response?.toUpperCase()}</strong>
                  {myResponse.lunch_response && ` · Lunch: ${myResponse.lunch_response.toUpperCase()}`}
                </span>
              </div>
            )}
            {(windowClosed || isLocked) && !myResponse && (
              <div style={{ ...sectionStyle, border: '1px solid #fca5a5', background: '#fef2f2', padding: '12px 16px', display: 'flex', gap: 10, alignItems: 'center' }}>
                <span>⚠️</span>
                <span style={{ fontSize: '0.82rem', color: '#991b1b' }}>You did not respond — NO has been recorded.</span>
              </div>
            )}

            {/* ── Already responded confirmation ── */}
            {myResponse && !isLocked && !windowClosed && (
              <div style={{
                ...sectionStyle,
                border: `1px solid ${gameResponse === 'yes' ? '#86efac' : '#fca5a5'}`,
                background: gameResponse === 'yes' ? '#f0fdf4' : '#fef2f2',
                padding: '12px 18px',
                display: 'flex', gap: 10, alignItems: 'center',
              }}>
                <span style={{ fontSize: '1.1rem' }}>{gameResponse === 'yes' ? '✅' : '❌'}</span>
                <div style={{ fontSize: '0.82rem', color: gameResponse === 'yes' ? '#166534' : '#991b1b' }}>
                  <strong>Response recorded: {gameResponse?.toUpperCase()}</strong> · You can update until the window closes
                </div>
              </div>
            )}

            {/* ── Playing This Sunday ── */}
            <div style={sectionStyle}>
              <div style={sectionHeaderStyle()}>
                <span style={{ fontSize: '1.1rem' }}>🏏</span>
                <div>
                  <div style={{ fontFamily: 'Rajdhani, sans-serif', fontWeight: 700, fontSize: '1rem', color: NAVY }}>
                    Playing on {matchDate ? matchDate.toLocaleDateString('en-IN', { weekday:'long', day:'numeric', month:'short' }) : 'Sunday'}?
                  </div>
                  <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: 1 }}>
                    Not responding by 6 PM = NO + ₹1,000 fine
                  </div>
                </div>
                {myResponse && (
                  <div style={{
                    marginLeft: 'auto',
                    background: gameResponse === 'yes' ? '#dcfce7' : '#fee2e2',
                    color: gameResponse === 'yes' ? GREEN : RED,
                    fontFamily: 'Rajdhani, sans-serif',
                    fontWeight: 800,
                    fontSize: '0.75rem',
                    letterSpacing: '0.08em',
                    padding: '3px 10px',
                    borderRadius: 20,
                  }}>
                    {gameResponse?.toUpperCase()}
                  </div>
                )}
              </div>
              <div style={{ padding: '16px 18px', display: 'flex', gap: 10 }}>
                <ResponseBtn value="yes" selected={gameResponse} yesNo="yes"
                  onClick={setGameResponse} disabled={!canSubmit} />
                <ResponseBtn value="no"  selected={gameResponse} yesNo="no"
                  onClick={setGameResponse} disabled={!canSubmit} />
              </div>
            </div>

            {/* ── Lunch ── */}
            <div style={sectionStyle}>
              <div style={sectionHeaderStyle()}>
                <span style={{ fontSize: '1.1rem' }}>🍱</span>
                <div>
                  <div style={{ fontFamily: 'Rajdhani, sans-serif', fontWeight: 700, fontSize: '1rem', color: NAVY }}>
                    Joining for Lunch?
                  </div>
                  <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: 1 }}>
                    Independent from match — open to all
                  </div>
                </div>
                {myResponse?.lunch_response && (
                  <div style={{
                    marginLeft: 'auto',
                    background: lunchResponse === 'yes' ? '#dcfce7' : '#fee2e2',
                    color: lunchResponse === 'yes' ? GREEN : RED,
                    fontFamily: 'Rajdhani, sans-serif',
                    fontWeight: 800,
                    fontSize: '0.75rem',
                    letterSpacing: '0.08em',
                    padding: '3px 10px',
                    borderRadius: 20,
                  }}>
                    {lunchResponse?.toUpperCase()}
                  </div>
                )}
              </div>

              {/* Menu display */}
              {lunchMenu?.menu_text && (
                <div style={{ padding: '10px 18px 0', borderBottom: '1px solid #f1f5f9' }}>
                  {lunchMenu.menu_image_url && (
                    <img src={lunchMenu.menu_image_url} alt="Lunch menu"
                      style={{ width: '100%', borderRadius: 10, marginBottom: 10, maxHeight: 200, objectFit: 'cover' }} />
                  )}
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>Today's Menu</div>
                  <div style={{ fontSize: '0.88rem', color: NAVY, whiteSpace: 'pre-line', lineHeight: 1.7, marginBottom: 12 }}>
                    {lunchMenu.menu_text}
                  </div>
                </div>
              )}

              <div style={{ padding: '16px 18px', display: 'flex', gap: 10 }}>
                <ResponseBtn value="yes" selected={lunchResponse} yesNo="yes"
                  onClick={setLunchResponse} disabled={!canSubmit} />
                <ResponseBtn value="no"  selected={lunchResponse} yesNo="no"
                  onClick={setLunchResponse} disabled={!canSubmit} />
              </div>
            </div>

            {/* ── Special Requests ── */}
            {canSubmit && gameResponse === 'yes' && specialConditions.length > 0 && (
              <div style={sectionStyle}>
                <div style={sectionHeaderStyle()}>
                  <span style={{ fontSize: '1.1rem' }}>📝</span>
                  <div>
                    <div style={{ fontFamily: 'Rajdhani, sans-serif', fontWeight: 700, fontSize: '1rem', color: NAVY }}>
                      Any Special Requests?
                    </div>
                    <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: 1 }}>
                      Tick any limitations you have for this match
                    </div>
                  </div>
                </div>
                <div style={{ padding: '12px 18px 16px' }}>
                  {/* Master toggle */}
                  <label style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                    background: '#f8fafc', borderRadius: 10, cursor: 'pointer',
                    border: '1.5px solid ' + (hasSpecial ? GOLD : '#e2e8f0'),
                  }}>
                    <input
                      type="checkbox"
                      checked={hasSpecial}
                      onChange={e => {
                        const on = e.target.checked;
                        setHasSpecial(on);
                        if (!on) setRequestState({});
                      }}
                      style={{ width: 18, height: 18, accentColor: NAVY, cursor: 'pointer' }}
                    />
                    <span style={{ fontWeight: 600, color: NAVY, fontSize: '0.9rem' }}>
                      Yes, I have special requests
                    </span>
                  </label>

                  {/* Sub-options — rendered from specialConditions */}
                  {hasSpecial && (
                    <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {specialConditions.map(c => {
                        const cur = requestState[c.id] || { checked: false, member_input: '' };
                        const toggle = (checked) => setRequestState(s => ({
                          ...s,
                          [c.id]: { checked, member_input: checked ? (s[c.id]?.member_input || '') : '' },
                        }));
                        const setInput = (v) => setRequestState(s => ({
                          ...s,
                          [c.id]: { ...(s[c.id] || { checked: true }), member_input: v },
                        }));
                        return (
                          <div key={c.id}>
                            <label style={{
                              display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px',
                              background: cur.checked ? '#eff6ff' : 'white', borderRadius: 10, cursor: 'pointer',
                              border: '1px solid ' + (cur.checked ? NAVY : '#e2e8f0'),
                            }}>
                              <input
                                type="checkbox"
                                checked={!!cur.checked}
                                onChange={e => toggle(e.target.checked)}
                                style={{ width: 18, height: 18, accentColor: NAVY, cursor: 'pointer' }}
                              />
                              <span style={{ fontWeight: 600, color: NAVY, fontSize: '0.88rem', flex: 1 }}>
                                {c.label}
                                {c.is_standby && (
                                  <span style={{ fontSize:'0.6rem', fontWeight:800, background:'#fbbf24', color:'#78350f', padding:'1px 6px', borderRadius:8, marginLeft:6 }}>
                                    STANDBY
                                  </span>
                                )}
                              </span>
                            </label>

                            {/* Extra input — shown when ticked AND condition has input_type */}
                            {cur.checked && c.input_type === 'time' && (
                              <div style={{
                                marginTop: 6, padding: '11px 14px', background: '#fffbeb', borderRadius: 10,
                                border: '1px solid #fbbf24',
                              }}>
                                <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#92400e', marginBottom: 6 }}>
                                  ⏰ {c.input_label || 'Time'}
                                </div>
                                <input
                                  type="time"
                                  value={cur.member_input || ''}
                                  onChange={e => setInput(e.target.value)}
                                  style={{
                                    width: '100%', padding: '9px 12px', borderRadius: 8,
                                    border: '1.5px solid #fbbf24', fontSize: '0.95rem',
                                    fontFamily: 'DM Sans, sans-serif', color: NAVY,
                                    background: 'white', outline: 'none', boxSizing: 'border-box',
                                  }}
                                />
                                {c.is_standby && (
                                  <div style={{ fontSize: '0.68rem', color: '#92400e', marginTop: 6 }}>
                                    Requires Admin approval — you'll be on Standby until approved.
                                  </div>
                                )}
                              </div>
                            )}
                            {cur.checked && c.input_type === 'text' && (
                              <div style={{
                                marginTop: 6, padding: '11px 14px', background: '#f0f9ff', borderRadius: 10,
                                border: '1px solid #38bdf8',
                              }}>
                                <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#075985', marginBottom: 6 }}>
                                  ✏️ {c.input_label || 'Details'}
                                </div>
                                <input
                                  type="text"
                                  value={cur.member_input || ''}
                                  onChange={e => setInput(e.target.value)}
                                  maxLength={100}
                                  style={{
                                    width: '100%', padding: '9px 12px', borderRadius: 8,
                                    border: '1.5px solid #38bdf8', fontSize: '0.92rem',
                                    fontFamily: 'DM Sans, sans-serif', color: NAVY,
                                    background: 'white', outline: 'none', boxSizing: 'border-box',
                                  }}
                                />
                                {c.is_standby && (
                                  <div style={{ fontSize: '0.68rem', color: '#075985', marginTop: 6 }}>
                                    Requires Admin approval — you'll be on Standby until approved.
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Submit Button ── */}
            {canSubmit && (
              <button
                onClick={handleSubmit}
                disabled={submitting || !gameResponse || !lunchResponse}
                style={{
                  width: '100%',
                  padding: '16px',
                  borderRadius: 14,
                  border: 'none',
                  background: (!gameResponse || !lunchResponse)
                    ? '#e2e8f0'
                    : `linear-gradient(135deg, ${NAVY}, #0f2347)`,
                  color: (!gameResponse || !lunchResponse) ? '#94a3b8' : 'white',
                  fontFamily: 'Rajdhani, sans-serif',
                  fontWeight: 800,
                  fontSize: '1rem',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  cursor: (!gameResponse || !lunchResponse || submitting) ? 'not-allowed' : 'pointer',
                  marginBottom: 16,
                  boxShadow: (!gameResponse || !lunchResponse) ? 'none' : '0 4px 14px rgba(30,58,110,0.3)',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                }}
              >
                {submitting
                  ? <><span style={{ display:'inline-block', width:16, height:16, border:'2px solid rgba(255,255,255,0.4)', borderTopColor:'white', borderRadius:'50%', animation:'spin 0.7s linear infinite' }} /> Saving…</>
                  : myResponse ? '↻  Update Response' : '✓  Submit Availability'
                }
              </button>
            )}

            {/* ── Opt Out ── */}
            {canOptOut && (
              <div style={{
                ...sectionStyle,
                border: `1.5px solid ${GOLD}33`,
                background: 'linear-gradient(135deg, #fffdf0, #fffbe6)',
              }}>
                <div style={{ padding: '18px 18px', textAlign: 'center' }}>
                  <div style={{ fontSize: '2rem', marginBottom: 8 }}>🙋</div>
                  <div style={{ fontFamily: 'Rajdhani, sans-serif', fontWeight: 700, fontSize: '1rem', color: NAVY, marginBottom: 4 }}>
                    Volunteer to Opt Out
                  </div>
                  <p style={{ fontSize: '0.82rem', color: '#6b7280', marginBottom: 4, lineHeight: 1.5 }}>
                    We have more members than needed this Sunday. Volunteers who sit out will receive a benefit.
                  </p>
                  <p style={{ fontSize: '0.75rem', color: '#9a7c0a', marginBottom: 16 }}>
                    {optOutStatus.opt_out_type === 'opt_out_full'
                      ? '✅ Benefit: Fee waived + attendance marked as present'
                      : '✅ Benefit: Attendance counted as present for fee purposes'}
                    {' · '}Window closes Saturday 10:00 AM.
                  </p>
                  <button
                    onClick={handleOptOut}
                    disabled={submitting}
                    style={{
                      padding: '11px 24px',
                      borderRadius: 10,
                      border: `2px solid ${GOLD}`,
                      background: optedOut ? GOLD : 'transparent',
                      color: optedOut ? NAVY : '#b45309',
                      fontFamily: 'Rajdhani, sans-serif',
                      fontWeight: 700,
                      fontSize: '0.9rem',
                      letterSpacing: '0.05em',
                      cursor: 'pointer',
                    }}
                  >
                    {optedOut ? '↩ Cancel Opt Out' : '🙋 Volunteer to Opt Out'}
                  </button>
                </div>
              </div>
            )}

            {/* ── Match Info ── */}
            <div style={sectionStyle}>
              <div style={{ ...sectionHeaderStyle(), paddingBottom: 10 }}>
                <span style={{ fontSize: '1rem' }}>📋</span>
                <div style={{ fontFamily: 'Rajdhani, sans-serif', fontWeight: 700, fontSize: '0.9rem', color: NAVY }}>
                  Match Details
                </div>
              </div>
              <div style={{ padding: '4px 18px 18px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 12px' }}>
                {[
                  { label: 'Format',         value: match.match_type ? `${match.match_type} Rules` : '—' },
                  { label: 'Reporting Time', value: match.reporting_time?.slice(0,5) || '07:30' },
                  { label: 'Overs',          value: `${match.overs || 20} overs` },
                  { label: 'Venue',          value: match.venue || 'UCC Ground' },
                  ...(match.series_name ? [{ label: 'Series', value: match.series_name }] : []),
                ].map(({ label, value }) => (
                  <div key={label}>
                    <div style={labelStyle}>{label}</div>
                    <div style={{ fontFamily: 'Rajdhani, sans-serif', fontWeight: 600, fontSize: '0.95rem', color: NAVY, marginTop: 2 }}>
                      {value}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        button:hover:not(:disabled) { transform: translateY(-1px); }
      `}</style>
    </div>
  );
}
